import { Activity, Building2, CheckCircle, ClipboardList, Clock, Package2, MoreHorizontal, ToggleLeft, ToggleRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import ActionMenu from "../../../components/shared/ActionMenu";
import DataTable from "../../../components/shared/DataTable";
import StatusBadge from "../../../components/shared/StatusBadge";
import { DropdownMenuItem } from "../../../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Switch } from "../../../components/ui/switch.jsx";
import { Label } from "../../../components/ui/label.jsx";
import { useAuth } from "../../../context/AuthContext";
import { useProjectFilter } from "../../../hooks/useProjectFilter";
import { formatDate } from "../../../utils/dateUtils";

const ActivityTable = ({
  activities,
  onEdit,
  onUpdateStatus,
  sortBy,
  direction,
  onSort,
  showDeleted,
  setShowDeleted,
  setPage
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { updateProject } = useProjectFilter();

  const handleProjectLink = (projectId, path) => {
    updateProject(projectId);
    navigate(path);
  };

  const activityColumns = [
    {
      header: "Title",
      accessor: "title",
      isPrimary: true,
      render: (val, activity) => (
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-semibold text-slate-900 dark:text-slate-100 block">{val}</span>
            {activity.deleted && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 uppercase tracking-wider shrink-0">
                Deleted
              </span>
            )}
          </div>
          <span className="text-sm text-slate-500 dark:text-slate-400 truncate max-w-[250px] md:max-w-md block">
            {activity.description}
          </span>
        </div>
      )
    },
    {
      header: "Date",
      accessor: "date",
      sortable: true,
      sortKey: "date",
      render: (val) => formatDate(val)
    },
    {
      header: "Project",
      accessor: "project",
      render: (val) => val?.name || "N/A"
    },
    {
      header: "Assigned To",
      accessor: "assignedTo",
      render: (val) => val?.name || "Unknown"
    },
    {
      header: "Status",
      accessor: "status",
      render: (val) => <StatusBadge status={val} />
    },
    {
      header: "Actions",
      filter: user?.permissions?.activity?.delete ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${showDeleted ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="w-4 h-4 hidden md:block" />
              <span className="md:hidden">
                {showDeleted ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3" align="end">
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={showDeleted}
                onCheckedChange={(val) => {
                  setShowDeleted(val);
                  setPage(0);
                }}
                id="show-deleted-activities-table"
              />
              <Label htmlFor="show-deleted-activities-table" className="text-xs font-semibold cursor-pointer whitespace-nowrap">
                Show Deleted
              </Label>
            </div>
          </PopoverContent>
        </Popover>
      ) : null,
      accessor: "id",
      render: (_, activity) => (
        <ActionMenu
          canEdit={user?.permissions?.activity?.update && !activity.deleted}
          canDelete={false}
          onEdit={() => onEdit(activity.id)}
          onDelete={null}
          menuWidth="w-56"
          quickLinksLabel="Project Links"
          customActions={
            activity.status?.toLowerCase() !== "completed" && user?.permissions?.activity?.update && !activity.deleted ? (
              <>
                <DropdownMenuItem
                  onClick={() => onUpdateStatus(activity.id, "in_progress")}
                  disabled={activity.status?.toLowerCase() === "in_progress"}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Clock className="w-4 h-4 text-yale-blue-500" />
                  <span>In Progress</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onUpdateStatus(activity.id, "completed")}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Mark Completed</span>
                </DropdownMenuItem>
              </>
            ) : null
          }
          quickLinks={
            activity.project?.id || activity.projectId ? (
              <>
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => handleProjectLink(activity.project?.id || activity.projectId, "/tasks")}
                >
                  <ClipboardList className="w-4 h-4 mr-2" /> Tasks
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => handleProjectLink(activity.project?.id || activity.projectId, "/materials")}
                >
                  <Package2 className="w-4 h-4 mr-2" /> Materials
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() =>
                    handleProjectLink(
                      activity.project?.id || activity.projectId,
                      `/projects/${activity.project?.id || activity.projectId}`
                    )
                  }
                >
                  <Building2 className="w-4 h-4 mr-2" /> Project
                </DropdownMenuItem>
              </>
            ) : null
          }
        />
      )
    }
  ];

  return (
    <DataTable
      columns={activityColumns}
      data={activities}
      emptyStateTitle="No activities logged"
      emptyStateDescription="Click 'Log Activity' to add your first activity."
      emptyStateIcon={Activity}
      sortBy={sortBy}
      direction={direction}
      onSort={onSort}
    />
  );
};

export default ActivityTable;
